import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useClassCode } from '../context/ClassCodeContext'
import { useStrings } from '../site/strings'
import { Bookmark } from '../site/Bookmark'
import { ChalkMark } from '../site/ChalkMark'
import { formatDay, todayISO } from '../site/dates'
import '../site/site.css'

// The class code asks the game server; a right code opens that class's board.
// With ?add=1 a student who already has a class adds another one. With ?invite= (a link the teacher made for friends
// and family) the Guests class opens without a code.
export function ClassCodeGatePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const adding = params.get('add') === '1'
  const { unlock, isUnlocked, passes, forget, redeem } = useClassCode()
  const { s, lang, toggle } = useStrings()
  const [error, setError] = useState<string | null>(null)
  const invite = params.get('invite')
  const [busy, setBusy] = useState(!!invite)

  useEffect(() => {
    if (!invite) return
    redeem(invite).then((r) => {
      if (r === 'ok') return navigate('/play', { replace: true })
      setBusy(false)
      setError(r === 'expired' ? s.inviteExpired : r === 'wrong' ? s.inviteWrong : r === 'wait' ? s.waitCode : s.unreachable)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite])

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/board'

  useEffect(() => {
    if (isUnlocked && !adding && !busy) navigate(from, { replace: true })
  }, [isUnlocked, adding, busy, navigate, from])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (busy) return
    const code = String(new FormData(event.currentTarget).get('classCode') ?? '').trim()
    if (!code) return setError(s.wrongCode)
    setBusy(true)
    setError(null)
    const { result, section } = await unlock(code)
    setBusy(false)
    if (result === 'ok') {
      if (section) localStorage.setItem('gs.class', section)
      navigate(adding ? '/board' : from, { replace: true })
    } else setError(result === 'wrong' ? s.wrongCode : result === 'wait' ? s.waitCode : s.unreachable)
  }

  const remove = (section: string, name: string) => {
    if (!window.confirm(s.removeConfirm.replace('{name}', name))) return
    forget(section)
    if (localStorage.getItem('gs.class') === section) localStorage.removeItem('gs.class')
  }

  // The first thing on the board: today's date chalked in the corner, last lesson's notes rubbed out behind,
  // and the mark drawing itself on above the code box. Everything settles and holds still.
  return (
    <div className="site gate">
      <div className="gate-ghost" aria-hidden="true">
        <ChalkMark kind="ghost" pad={0} seed={11} alpha={0.13} dur={0.01} />
      </div>
      <p className="gate-date">
        <span className="mark-host">
          {formatDay(todayISO(), lang, 'long')}
          <ChalkMark kind="underline" pad={8} seed={3} delay={0.2} dur={0.5} width={2.2} alpha={0.8} />
        </span>
      </p>
      <button className="btn quiet gate-lang" type="button" onClick={toggle} aria-label={s.languageLabel}>
        {s.language}
      </button>
      <main className="gate-main">
        <form className="join gate-form" onSubmit={handleSubmit}>
          <div className="gate-head" translate="no">
            <Bookmark drawn size={104} />
            <h1 className="gate-name">学しおり</h1>
            <p className="gate-en">GakuShiori</p>
          </div>
          {adding && <h2 className="gate-sub">{s.manageClasses}</h2>}
          {adding && passes.length > 0 && (
            <div>
              <h3 className="my-classes-head">{s.yourClasses}</h3>
              <ul className="my-classes">
                {passes.map((p) => (
                  <li key={p.section}>
                    <span translate="no">{p.name}</span>
                    <button className="btn quiet" type="button" onClick={() => remove(p.section, p.name)}>
                      {s.remove}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <label htmlFor="class-code">{s.codeLabel}</label>
            <div className="gate-box">
              <ChalkMark kind="box" pad={8} seed={9} delay={0.9} dur={0.6} alpha={0.9} />
              <input
                id="class-code"
                name="classCode"
                type="text"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                aria-describedby="class-code-help"
                onChange={() => setError(null)}
              />
            </div>
            <p className="gate-help" id="class-code-help">
              {adding ? s.addIntro : s.codeIntro}
            </p>
          </div>
          {error && (
            <p className="problem" role="alert">
              {error}
            </p>
          )}
          <div className="gate-actions">
            <button className="btn go" type="submit" disabled={busy}>
              {busy ? s.checking : s.continue}
            </button>
            {adding && isUnlocked && (
              <Link className="btn quiet" to="/board">
                {s.board}
              </Link>
            )}
          </div>
        </form>
      </main>
      {!adding && <p className="gate-foot">{s.lostCode}</p>}
    </div>
  )
}
